// Egyptian national ID (14 digits): C YYMMDD GG SSSG K
//   C    – century: 2 = 1900s, 3 = 2000s
//   YYMMDD – date of birth
//   GG   – governorate of birth (see GOVERNORATES)
//   SSSG – serial; the last of these 4 digits (13th overall) is odd for
//          male, even for female
//   K    – check digit (not validated here)

const GOVERNORATES = {
  "01": { en: "Cairo", ar: "القاهرة" },
  "02": { en: "Alexandria", ar: "الإسكندرية" },
  "03": { en: "Port Said", ar: "بورسعيد" },
  "04": { en: "Suez", ar: "السويس" },
  "11": { en: "Damietta", ar: "دمياط" },
  "12": { en: "Dakahlia", ar: "الدقهلية" },
  "13": { en: "Sharqia", ar: "الشرقية" },
  "14": { en: "Qalyubia", ar: "القليوبية" },
  "15": { en: "Kafr El Sheikh", ar: "كفر الشيخ" },
  "16": { en: "Gharbia", ar: "الغربية" },
  "17": { en: "Monufia", ar: "المنوفية" },
  "18": { en: "Beheira", ar: "البحيرة" },
  "19": { en: "Ismailia", ar: "الإسماعيلية" },
  "21": { en: "Giza", ar: "الجيزة" },
  "22": { en: "Beni Suef", ar: "بني سويف" },
  "23": { en: "Fayoum", ar: "الفيوم" },
  "24": { en: "Minya", ar: "المنيا" },
  "25": { en: "Assiut", ar: "أسيوط" },
  "26": { en: "Sohag", ar: "سوهاج" },
  "27": { en: "Qena", ar: "قنا" },
  "28": { en: "Aswan", ar: "أسوان" },
  "29": { en: "Luxor", ar: "الأقصر" },
  "31": { en: "Red Sea", ar: "البحر الأحمر" },
  "32": { en: "New Valley", ar: "الوادي الجديد" },
  "33": { en: "Matrouh", ar: "مطروح" },
  "34": { en: "North Sinai", ar: "شمال سيناء" },
  "35": { en: "South Sinai", ar: "جنوب سيناء" },
  "88": { en: "Outside Egypt", ar: "خارج الجمهورية" },
};

const pad = (n) => String(n).padStart(2, "0");

// Arabic-Indic digits (٠-٩) → ASCII, so IDs typed on an Arabic keyboard parse.
export const normalizeNationalId = (raw) =>
  String(raw ?? "")
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/\s+/g, "");

/**
 * Returns { dateOfBirth: "YYYY-MM-DD", age, gender: "Male"|"Female",
 * governorate: { en, ar } } for a valid 14-digit Egyptian national ID,
 * or null when the value is incomplete / not a plausible ID.
 */
export const parseEgyptianNationalId = (raw) => {
  const id = normalizeNationalId(raw);
  if (!/^[23]\d{13}$/.test(id)) return null;

  const year = (id[0] === "2" ? 1900 : 2000) + Number(id.slice(1, 3));
  const month = Number(id.slice(3, 5));
  const day = Number(id.slice(5, 7));
  const birth = new Date(year, month - 1, day);
  if (
    birth.getFullYear() !== year ||
    birth.getMonth() !== month - 1 ||
    birth.getDate() !== day
  ) {
    return null; // e.g. month 13 or Feb 30
  }

  const today = new Date();
  if (birth > today) return null;

  const governorate = GOVERNORATES[id.slice(7, 9)];
  if (!governorate) return null;

  let age = today.getFullYear() - year;
  if (today.getMonth() < month - 1 || (today.getMonth() === month - 1 && today.getDate() < day)) {
    age -= 1;
  }

  return {
    dateOfBirth: `${year}-${pad(month)}-${pad(day)}`,
    age,
    gender: Number(id[12]) % 2 === 1 ? "Male" : "Female",
    governorate,
  };
};
